import axios from 'axios';
import { User, UserRegistration, UserLogin, AuthResponse, UserRole } from '../types/user';
import { ApiConfig } from './api';

const API_URL = `${ApiConfig.baseUrl}/users`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('cqi_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Register a new user
 * @param userData User registration data
 * @param preserveSession If true, don't replace current user session (for admin creating users)
 * @returns Promise with auth response containing user and token
 * @throws Error if registration fails
 */
export const registerUser = async (userData: UserRegistration, preserveSession = false): Promise<AuthResponse> => {
  // Create a copy of userData to modify
  const userDataToSend = {...userData};
  
  if (userData.role === 'SUBCOUNTY_USER') {
    if (!userData.county || !userData.subCounty) {
      throw new Error('Subcounty users must have both county and subcounty specified');
    }
    
    userDataToSend.role = UserRole.COUNTY_USER;
    
    // Store the actual role information in the database by adding a note to the name
    userDataToSend.name = `${userData.name} (SubCounty User)`;
  }
  try {
    // Try the API call first
    try {
      const response = await axios.post<AuthResponse>(`${API_URL}/register`, userDataToSend);
      
      // For subcounty users, restore the original role in the response data
      if (userData.role === UserRole.SUBCOUNTY_USER && response.data.user) {
        response.data.user.role = UserRole.SUBCOUNTY_USER;
      }
      
      // Store token in local storage ONLY if we're not preserving the current session
      // This prevents admin session from being overwritten when creating new users
      if (response.data.token && !preserveSession) {
        localStorage.setItem('cqi_token', response.data.token);
        localStorage.setItem('cqi_user', JSON.stringify(response.data.user));
      }
      
      return response.data;
    } catch (apiError) {
      // API call failed, using mock implementation instead
      console.warn('API registration failed, using mock implementation');
      
      // Mock implementation using localStorage
      // Generate a unique ID
      const mockUserId = `user_${Date.now()}`;
      const mockToken = `mock_token_${Math.random().toString(36).substring(2)}`;
      
      // Create user object
      const user: User = {
        _id: mockUserId,
        name: userDataToSend.name, // Use the modified name that might include "(SubCounty User)" suffix
        email: userDataToSend.email,
        role: userData.role, // Keep the original role from frontend (including SUBCOUNTY_USER)
        county: userDataToSend.county || '',
        subCounty: userDataToSend.subCounty || '',
        facility: userDataToSend.facility || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      

    
      
      
      // Get existing users or initialize empty array
      const existingUsersStr = localStorage.getItem('cqi_mock_users');
      const existingUsers: User[] = existingUsersStr ? JSON.parse(existingUsersStr) : [];
      
      // Add new user to array
      existingUsers.push(user);
      
      
      
      // Save updated users array with proper JSON.stringify
      try {
  localStorage.setItem('cqi_mock_users', JSON.stringify(existingUsers));
} catch (e) {
  console.error('Error saving user data to localStorage:', e);
  throw new Error('Failed to save user data: ' + (e as Error).message);
}
      
      // Store token in local storage ONLY if we're not preserving the current session
      // This prevents admin session from being overwritten when creating new users
      if (!preserveSession) {
        localStorage.setItem('cqi_token', mockToken);
        localStorage.setItem('cqi_user', JSON.stringify(user));
      }
      
      // Return mock response
      return {
        token: mockToken,
        user: user
      };
    }
  } catch (error: any) {
    console.error('Unexpected registration error', error);
    throw new Error('Registration failed: ' + (error.message || 'Unknown error'));
  }
};

/**
 * Login a user
 * @param userData User login credentials
 * @returns Promise with auth response containing user and token
 */
export const loginUser = async (userData: UserLogin): Promise<AuthResponse> => {
  
  try {
    const response = await axios.post<AuthResponse>(`${API_URL}/login`, userData);
    
    // Store token in local storage
    if (response.data.token) {
      localStorage.setItem('cqi_token', response.data.token);
      localStorage.setItem('cqi_user', JSON.stringify(response.data.user));
    }
    
    return response.data;
  } catch (error: any) {
    console.error('Login failed:', error);
    throw error;
  }
};

/**
 * Logout current user
 */
export const logoutUser = (): void => {
  localStorage.removeItem('cqi_token');
  localStorage.removeItem('cqi_user');
};

/**
 * Update user password
 * @param currentPassword User's current password
 * @param newPassword User's new password
 * @returns Promise with success message
 */
export const updatePassword = async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
  const response = await axios.post<{ message: string }>(
    `${API_URL}/update-password`,
    { currentPassword, newPassword },
    { headers: getAuthHeaders() }
  );

  return response.data;
};

/**
 * Set password for the first time (for OIDC/SSO users who don't have a local password)
 * @param newPassword New local password
 * @returns Promise with success message
 */
export const setPassword = async (newPassword: string): Promise<{ message: string }> => {
  const response = await axios.post<{ message: string }>(
    `${API_URL}/set-password`,
    { newPassword },
    { headers: getAuthHeaders() }
  );

  return response.data;
};

/**
 * Get current authenticated user
 * @returns Current user or null if not authenticated
 */
export const getCurrentUser = (): User | null => {
  const userJson = localStorage.getItem('cqi_user');
  if (userJson) {
    const user = JSON.parse(userJson) as User;
    
    // Check if this is a subcounty user masquerading as a county user
    if (user.role === UserRole.COUNTY_USER && user.name && user.name.includes('(SubCounty User)')) {
      const processedUser = {...user};
      // Set the correct role
      processedUser.role = UserRole.SUBCOUNTY_USER;
      // Remove the suffix from name
      processedUser.name = processedUser.name.replace(' (SubCounty User)', '');
      return processedUser;
    }
    
    return user;
  }
  return null;
};

/**
 * Get all users (CQI Manager only)
 * @returns Promise with array of users
 */
export const getAllUsers = async (): Promise<User[]> => {
  try {
    const token = localStorage.getItem('cqi_token');
    try {
      // Try API call first
      const response = await axios.get<User[]>(API_URL, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Process users to identify subcounty users by name suffix
      const processedUsers = response.data.map(user => {
        // Check if the name has the subcounty user suffix
        if (user.name && user.name.includes('(SubCounty User)') && user.role === UserRole.COUNTY_USER) {
          // Clone the user to avoid mutating the response directly
          const processedUser = {...user};
          // Set the role to SUBCOUNTY_USER
          processedUser.role = UserRole.SUBCOUNTY_USER;
          // Optionally remove the suffix from the name
          processedUser.name = processedUser.name.replace(' (SubCounty User)', '');
          return processedUser;
        }
        return user;
      });
      
      return processedUsers;
    } catch (apiError) {
      // API failed, use mock localStorage data
      console.warn('API failed to fetch users, using mock implementation');
      
      // Get users from localStorage
      const existingUsersStr = localStorage.getItem('cqi_mock_users');
      if (existingUsersStr) {
        const users = JSON.parse(existingUsersStr);
        
        // Process users to ensure consistency with the API implementation
        const processedUsers = users.map((user: User) => {
          // For users marked as COUNTY_USER with the subcounty suffix in name
          if (user.name && user.name.includes('(SubCounty User)') && user.role === UserRole.COUNTY_USER) {
            const processedUser = {...user};
            processedUser.role = UserRole.SUBCOUNTY_USER;
            processedUser.name = processedUser.name.replace(' (SubCounty User)', '');
            return processedUser;
          }
          return user;
        });
        
       
        
        return processedUsers;
      }
      
      // Return empty array if no users exist
      return [];
    }
  } catch (error: any) {
    console.error('Failed to fetch users:', error);
    throw new Error('Failed to fetch users');
  }
};

/**
 * Get user profile
 * @returns Promise with user profile
 */
export const getUserProfile = async (): Promise<User> => {
  const token = localStorage.getItem('cqi_token');
  const response = await axios.get<User>(`${API_URL}/me`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

/**
 * Update a user
 * @param id User ID
 * @param userData Updated user data
 * @returns Promise with updated user
 */
export const updateUser = async (id: string, userData: Partial<User>): Promise<User> => {
  const token = localStorage.getItem('cqi_token');
  const response = await axios.patch<User>(`${API_URL}/${id}`, userData, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

/**
 * Delete a user
 * @param id User ID
 * @returns Promise with success message
 */
export const deleteUser = async (id: string): Promise<{ message: string }> => {
  const token = localStorage.getItem('cqi_token');
  const userStr = localStorage.getItem('cqi_user');
  const user = userStr ? JSON.parse(userStr) : null;
  
  
  try {
    const response = await axios.delete<{ message: string }>(`${API_URL}/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error: any) {
    throw error;
  }
};
